import { useState, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { useDebounce } from 'rooks';

import { isCompletionIndicator, isEmailAdditionKey, KEYBOARD_KEYS } from 'utils/keyboardUtils';

import {
  isEmailFormat,
  isMatrixIdFormat,
  isEmailOrMatrixIdFormat,
  isInviteeDuplicate,
  normalizeMatrixId,
} from './utils';

interface UseInviteInputLogicProps {
  invitees: string[];
  addInvitee: (invitee: string) => void;
  removeInvitee: (invitee: string) => void;
  setInvitees: (invitees: string[]) => void;
}

interface UseInviteInputLogicReturn {
  inputValue: string;
  setInputValue: (value: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  handleInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  isCurrentInputDuplicate: boolean;
}

const useInviteInputLogic = ({
  invitees,
  addInvitee,
  removeInvitee,
  setInvitees,
}: UseInviteInputLogicProps): UseInviteInputLogicReturn => {
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const trimmedInputValue = inputValue.trim();
  const isCurrentInputDuplicate = isInviteeDuplicate(trimmedInputValue, invitees);

  const handleAutoConvert = (value: string) => {
    const normalized = normalizeMatrixId(value);
    if (isEmailOrMatrixIdFormat(normalized) && inputValue.trim() === value) {
      const normalizedValue = isEmailFormat(normalized) ? normalized.toLowerCase() : normalized;
      if (!invitees.includes(normalizedValue)) {
        addInvitee(normalizedValue);
        setInputValue('');
      }
    }
  };

  const debouncedAutoConvert = useDebounce(handleAutoConvert, 1500);

  const addEmail = (input: string) => {
    const trimmedInput = normalizeMatrixId(input.trim());
    const normalizedInput = isEmailFormat(trimmedInput) ? trimmedInput.toLowerCase() : trimmedInput;

    if (!trimmedInput || invitees.includes(normalizedInput) || !isEmailOrMatrixIdFormat(trimmedInput)) return;

    addInvitee(normalizedInput);
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEmailAdditionKey(e.key)) {
      e.preventDefault();
      if (trimmedInputValue) addEmail(inputValue);
    } else if (
      (e.key === KEYBOARD_KEYS.BACKSPACE || e.key === KEYBOARD_KEYS.ARROW_LEFT) &&
      !inputValue &&
      !!invitees.length
    ) {
      // Put the last invitee back in the input when pressing backspace or left arrow key
      const lastInvitee = invitees[invitees.length - 1];
      if (lastInvitee) {
        e.preventDefault();
        removeInvitee(lastInvitee);
        debouncedAutoConvert.cancel();
        setInputValue(lastInvitee);
      }
    }
  };

  const handleInputChange = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(value);

    // Auto add to form state and convert to chip when a valid email or Matrix ID is detected
    const lastChar = value.slice(-1);
    const inputToAdd = value.slice(0, -1);
    if (isCompletionIndicator(lastChar) && (isEmailFormat(value) || isMatrixIdFormat(value))) {
      addEmail(inputToAdd);
    }

    // Trigger debounced auto-conversion for valid inputs (pass raw trimmed value;
    // handleAutoConvert normalizes internally so the stale-input guard compares correctly)
    const trimmedNewValue = value.trim();
    if (trimmedNewValue && isEmailOrMatrixIdFormat(normalizeMatrixId(trimmedNewValue))) {
      debouncedAutoConvert(trimmedNewValue);
    }
  };

  // Handle pasted string of items
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    const items = pastedText
      .split(/[,;\n\s]+/)
      .map((item) => normalizeMatrixId(item.trim()))
      .filter((item) => item.length > 0);

    const validItems = items.filter((item) => isEmailOrMatrixIdFormat(item));

    if (validItems.length > 0) {
      const newInvitees = [...invitees];

      validItems.forEach((item) => {
        const normalizedItem = isEmailFormat(item) ? item.toLowerCase() : item;
        if (!newInvitees.includes(normalizedItem)) newInvitees.push(normalizedItem);
      });

      setInvitees(newInvitees);
    }

    setInputValue('');
  };

  return {
    inputValue,
    setInputValue,
    textareaRef,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    isCurrentInputDuplicate,
  };
};

export default useInviteInputLogic;
